/**
 * PRICING — SINGLE SOURCE OF TRUTH (canonical spec: docs/aura_pricing_final_and_site_edits_2026-07.md)
 *
 * Every rendered price, tier allowance, and pricing copy block on the site
 * must come from this file. tests/pricing.test.ts enforces that no literal
 * price strings exist outside it. Server charge amounts (cents) live in
 * server.ts TIER_CONFIG; the test asserts they agree with these values.
 *
 * Canonical lineup (locked 2026-07-04):
 *   Day Planner Free · Day Pass $9 · Pro $99/yr (20 itineraries) ·
 *   Atelier $299/mo or $2,990/yr (30/mo) · Founding Partner $199/mo (10 seats) ·
 *   Groups/Enterprise from $999/mo.
 * Never advertise "unlimited" itineraries for a metered tier.
 */

export const PRICING = {
  dayPlanner: {
    key: 'free',
    name: 'Day Planner',
    price: 0,
    cadence: 'forever',
  },
  dayPass: {
    key: 'day_pass',
    name: 'Day Pass',
    price: 9,
    cadence: 'one itinerary',
    /** server.ts checkout tierId */
    checkoutTier: 'day_pass',
    askAuraMessages: 10,
  },
  pro: {
    key: 'pro',
    name: 'Aura Pro',
    priceYearly: 99,
    itinerariesPerYear: 20,
    /** 99 / 20 — the framing that writes its own ad */
    perTrip: 4.95,
    checkoutTier: 'membership',
  },
  atelier: {
    key: 'atelier',
    name: 'Atelier',
    priceMonthly: 299,
    priceAnnual: 2990,
    itinerariesPerMonth: 30,
    /** server.ts checkout tierId */
    checkoutTier: 'boutique',
  },
  foundingPartner: {
    key: 'founding_partner',
    name: 'Founding Partner',
    priceMonthly: 199,
    seatsTotal: 10,
    /** Update by hand as seats fill; drives every public counter. */
    seatsRemaining: 7,
    deadline: 'September 30',
  },
  enterprise: {
    key: 'enterprise',
    name: 'Groups & Enterprise',
    fromMonthly: 999,
  },
} as const;

/** "$2,990" style formatting for whole-dollar amounts. */
export const fmt = (n: number): string => `$${n.toLocaleString('en-US')}`;

/**
 * Public Founding Partner urgency label.
 *
 * TRUST/COMPLIANCE: Founding Partner is not yet purchasable in-app (the server
 * checkout map sells only day_pass/Pro/Atelier), so there are zero subscriptions
 * to count — a live "N of 10 remaining" counter would be unbacked false scarcity.
 * Until a real purchase path + Stripe seat count exists, show durable, non-numeric
 * urgency. seatsTotal/seatsRemaining stay in PRICING for when this is wired to
 * stripe.subscriptions.list against the founding price ID.
 */
export const foundingSeatsLabel = (): string => 'Limited founding seats';

const P = PRICING;

/**
 * Paste-ready copy blocks from the pricing doc (sections A–D).
 * Interpolated from PRICING so a price change propagates everywhere.
 */
export const COPY = {
  /** A1 — homepage price banner */
  homeBanner: `Day Planner free forever · Day Pass ${fmt(P.dayPass.price)} · Pro ${fmt(P.pro.priceYearly)}/yr`,

  /** Homepage consultant anchor (above the tier cards) */
  homeAnchor: `Traditional travel consultants charge $200–500 per itinerary. Aura delivers magazine-quality results — free to start, ${fmt(P.dayPass.price)} for premium multi-day itineraries.`,

  /** A1 — Pro tier card line (replaces "unlimited") */
  proCardLine: `${P.pro.itinerariesPerYear} premium itineraries a year — ${fmt(P.pro.perTrip)} per trip`,

  /** A1 — replaces the "~$4/mo" chip */
  proChip: `Best value · ${fmt(P.pro.perTrip)}/itinerary`,

  /** A2 — agency section pricing (stop hiding the price) */
  atelierBanner: `Atelier — ${fmt(P.atelier.priceMonthly)}/mo · ${P.atelier.itinerariesPerMonth} white-label itineraries`,
  enterpriseBanner: `Groups & Enterprise — from ${fmt(P.enterprise.fromMonthly)}/mo`,

  /** A3 — Founding Partner strip (homepage) */
  foundingStripHeadline: `Founding Partners — ${P.foundingPartner.seatsTotal} seats, then it's gone.`,
  foundingStripBody: `The first ${P.foundingPartner.seatsTotal} agencies & properties get Atelier at ${fmt(P.foundingPartner.priceMonthly)}/mo, locked for life, plus a direct line to the founder. In exchange: your honest results become our case study.`,
  foundingStripCta: 'Claim a Founding Seat',

  /** A5 — the defined guarantee (replaces the bare "100% satisfaction" bullet) */
  guarantee: `The Aura Guarantee: if your premium itinerary isn't the best travel document you've ever held, we'll regenerate it free or refund your purchase within 7 days. No forms, one email.`,
  guaranteeShort: '7-Day Regenerate-or-Refund Guarantee',

  /** B2 — Atelier column on the pricing page */
  atelierColumnBlurb: `${P.atelier.itinerariesPerMonth} white-label itineraries/mo — your brand, your colors, zero Aura watermark. Concierge Studio, team access, custom volume for groups.`,
  foundingColumnNote: `Founding Partner: ${fmt(P.foundingPartner.priceMonthly)}/mo for life — limited founding seats.`,

  /** B3 — consultant anchor, sharpened */
  anchor: `A human travel consultant runs $200–500 per itinerary. Aura Pro is ${fmt(P.pro.perTrip)} — same insider-level planning, ninety seconds, every link verified.`,

  /** B4 — Day Pass → Pro credit line */
  dayPassCredit: `Every Day Pass counts: upgrade to Pro within 30 days and your ${fmt(P.dayPass.price)} comes off.`,

  /** B5 — reassurance row under the Pro card */
  proReassurance: '7-day guarantee · Cancel anytime · Renews at the same price',

  /** C2 — Founding Partner offer, agency-flavored (replaces testimonials in P1-1) */
  foundingAgencyOffer: `We're onboarding our first ten partner agencies at ${fmt(P.foundingPartner.priceMonthly)}/mo locked for life. In return, your results — time saved, close rates, client reactions — become the case study. You get below-cost pricing forever; we get the proof. Fair trade.`,

  /** C3 — pricing block additions on /for-agencies */
  atelierAnnualLine: `Annual: ${fmt(P.atelier.priceAnnual)} (two months free)`,
  foundingSeatsLine: `Founding Partner: ${fmt(P.foundingPartner.priceMonthly)}/mo · limited founding seats`,
  startToday: 'Start today — live in your brand within 24 hours',

  /** C5 — agency ROI math */
  agencyRoi: `The math: ${P.atelier.itinerariesPerMonth} itineraries a month × 4 hours each = 120 hours of production. At $50/hr loaded cost that's $6,000/mo of labor. Atelier is ${fmt(P.atelier.priceMonthly)}. You do the division.`,

  /** D2 — /for-hotels pricing additions */
  foundingPropertyLine: `Founding Property: ${fmt(P.foundingPartner.priceMonthly)}/mo for life — limited to our first ten`,
  hotelGroupsLine: `Hotel groups: custom volume from ${fmt(P.enterprise.fromMonthly)}/mo`,

  /** D3 — hotel per-guest ROI math */
  hotelRoi: `Per-guest math: at ${P.atelier.itinerariesPerMonth} itineraries/mo, each branded guest dossier costs your property under $10 — less than the fruit plate at turndown, and the guest keeps it, shares it, and books their next stay because of it.`,

  /** C1 — honest, sourced stats bar for /for-agencies (replaces the
   *  unverifiable 4.2x / 420% / 10x claims) */
  agencyStats: [
    { stat: '90s', label: 'Itinerary generation' },
    { stat: '140+', label: 'Countries covered' },
    { stat: '16h → <1h', label: 'Per itinerary — Priceline/Harris research baseline' },
    { stat: '$4.5k/mo', label: 'Production labor replaced*' },
  ],
  agencyStatsFootnote: `*${P.atelier.itinerariesPerMonth} itineraries × 4 hours × $50/hr loaded agency cost per month — honest math, not a testimonial.`,
} as const;
